import React from 'react';
import moment from 'moment';
import get from 'lodash/get';
import gql from 'graphql-tag';
import { Query } from 'react-apollo';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Link,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: 13,
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  body: {
    fontSize: 13,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
}))(TableRow);

const ADMIN_MLV_SCENARIOS_QUERY = gql`
  query AdminMultiLineScenarios($userId: ID!) {
    admin_multi_line_scenarios(user_id: $userId) {
      id
      name
      createdAt
      updatedAt
      author {
        id
        name
      }
      results
    }
  }
`;

// DCF Valuations Table (legacy)
const DcfValuationsTable = ({ discountedCashFlows }) => {
  const sortedDcfs = [...(discountedCashFlows || [])].reverse();

  if (sortedDcfs.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No DCF valuations found.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <StyledTableCell>Name</StyledTableCell>
            <StyledTableCell>Author</StyledTableCell>
            <StyledTableCell>Created</StyledTableCell>
            <StyledTableCell align="center">Action</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedDcfs.map((dcf, index) => (
            <StyledTableRow key={dcf.friendlyName || index}>
              <StyledTableCell>{get(dcf, 'name', 'Untitled')}</StyledTableCell>
              <StyledTableCell>{get(dcf, 'author.name', '-')}</StyledTableCell>
              <StyledTableCell>{moment(dcf.createdAt).format('MMM D, YYYY')}</StyledTableCell>
              <StyledTableCell align="center">
                <Link
                  href={`/valuations/dcf?dcf_id=${dcf.friendlyName}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  View <OpenInNewIcon style={{ fontSize: 14 }} />
                </Link>
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Format currency for valuation display
const formatValuation = (results) => {
  try {
    const mid = get(results, 'combined.mid');
    if (mid == null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(mid);
  } catch {
    return '-';
  }
};

// Multi-Line Valuation Scenarios Table (Valuation Studio)
const MlvScenariosTable = ({ userId, inventoryUserId }) => {
  if (!userId) {
    return (
      <Typography variant="body2" color="textSecondary">
        No user ID available.
      </Typography>
    );
  }

  return (
    <Query query={ADMIN_MLV_SCENARIOS_QUERY} variables={{ userId }} fetchPolicy="network-only">
      {({ loading, error, data }) => {
        if (loading) {
          return (
            <Box display="flex" alignItems="center" py={2} style={{ gap: 8 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="textSecondary">Loading scenarios...</Typography>
            </Box>
          );
        }

        if (error) {
          return (
            <Typography variant="body2" color="error">
              Error loading scenarios: {error.message}
            </Typography>
          );
        }

        const scenarios = get(data, 'admin_multi_line_scenarios', []);

        if (scenarios.length === 0) {
          return (
            <Typography variant="body2" color="textSecondary">
              No saved valuations found.
            </Typography>
          );
        }

        return (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Author</StyledTableCell>
                  <StyledTableCell>Value (Mid)</StyledTableCell>
                  <StyledTableCell>Last Updated</StyledTableCell>
                  <StyledTableCell align="center">Action</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scenarios.map((scenario) => (
                  <StyledTableRow key={scenario.id}>
                    <StyledTableCell>{scenario.name || 'Untitled'}</StyledTableCell>
                    <StyledTableCell>{get(scenario, 'author.name', '-')}</StyledTableCell>
                    <StyledTableCell>{formatValuation(scenario.results)}</StyledTableCell>
                    <StyledTableCell>{moment(scenario.updatedAt).format('MMM D, YYYY')}</StyledTableCell>
                    <StyledTableCell align="center">
                      <Link
                        href={`/admin/user/${userId}/valuation?source=user&scenario_id=${scenario.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        View <OpenInNewIcon style={{ fontSize: 14 }} />
                      </Link>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      }}
    </Query>
  );
};

// Main Widget Component
const ValuationScenariosWidget = ({ inventoryUser, company }) => {
  const hasMultiLineValuation = (get(company, 'feature_types', []) || []).includes('multi_line_valuation');
  const userId = get(inventoryUser, 'user.id');
  const discountedCashFlows = get(inventoryUser, 'discounted_cash_flows', []);

  return (
    <Box mb={3}>
      <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>
        {hasMultiLineValuation ? 'Valuation Studio' : 'Valuations (DCF)'}
      </Typography>
      {hasMultiLineValuation ? (
        <MlvScenariosTable userId={userId} inventoryUserId={inventoryUser?.id} />
      ) : (
        <DcfValuationsTable discountedCashFlows={discountedCashFlows} />
      )}
    </Box>
  );
};

export default ValuationScenariosWidget;
